import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  Output,
} from '@angular/core';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { Margin } from '@freelancer/ui/margin';
import { FontColor, FontSize, FontWeight } from '@freelancer/ui/text';

export enum BannerAlertType {
  DEFAULT = 'default',
  ERROR = 'error',
  INFO = 'info',
  SUCCESS = 'success',
  WARNING = 'warning',
  ANNOUNCEMENT_INFO = 'announcement-info',
  ANNOUNCEMENT_SUCCESS = 'announcement-success',
  ANNOUNCEMENT_CRITICAL = 'announcement-critical',
}

export interface BannerAlertButton {
  text: string;
  color?: ButtonColor;
  link?: string;
  fragment?: string;
  queryParams?: { [k: string]: any };
  linkActive?: string[] | string;
  linkActiveOptions?: { exact: boolean };
  busy?: boolean;
}

@Component({
  selector: 'fl-banner-alert',
  template: `
    <fl-bit
      #alertBox
      class="BannerAlertBox"
      [attr.data-type]="type"
      [flMarginBottom]="Margin.XXSMALL"
      role="alert"
      [@hideElement]="state"
      (@hideElement.done)="animationDone($event)"
    >
      <fl-bit
        *ngIf="!announcementBar && type !== BannerAlertType.DEFAULT"
        [ngSwitch]="type"
        [flMarginRight]="Margin.XSMALL"
      >
        <fl-icon
          *ngSwitchCase="BannerAlertType.INFO"
          [name]="'ui-info'"
          [color]="IconColor.PRIMARY"
        ></fl-icon>
        <fl-icon
          *ngSwitchCase="BannerAlertType.ERROR"
          [name]="'ui-warning'"
          [color]="IconColor.ERROR"
        ></fl-icon>
        <fl-icon
          *ngSwitchCase="BannerAlertType.SUCCESS"
          [name]="'ui-ok'"
          [color]="IconColor.SUCCESS"
        ></fl-icon>
        <fl-icon
          *ngSwitchCase="BannerAlertType.WARNING"
          [name]="'ui-warning-alt'"
          [color]="IconColor.CONTEST"
        ></fl-icon>
      </fl-bit>
      <fl-bit
        *ngIf="announcementBar"
        class="AnnouncementIcon"
        [flMarginRight]="Margin.SMALL"
      >
        <fl-icon [name]="announcementIcon" [color]="IconColor.LIGHT"></fl-icon>
      </fl-bit>
      <fl-bit [flMarginRight]="Margin.LARGE">
        <fl-text
          *ngIf="title"
          [size]="FontSize.MID"
          [weight]="FontWeight.BOLD"
          [flMarginBottom]="Margin.XXXSMALL"
        >
          {{ title }}
        </fl-text>
        <fl-text
          [size]="FontSize.MID"
          [color]="!announcementBar ? FontColor.DARK : FontColor.LIGHT"
          [flMarginBottom]="bannerAlertButton ? Margin.XXSMALL : Margin.NONE"
        >
          <ng-content></ng-content>
        </fl-text>
        <fl-button
          *ngIf="bannerAlertButton"
          [color]="
            bannerAlertButton.color
              ? bannerAlertButton.color
              : ButtonColor.DEFAULT
          "
          [size]="ButtonSize.SMALL"
          [link]="bannerAlertButton.link"
          [linkActive]="bannerAlertButton.linkActive"
          [fragment]="bannerAlertButton.fragment"
          [queryParams]="bannerAlertButton.queryParams"
          [busy]="bannerAlertButton.busy"
          [linkActiveOptions]="bannerAlertButton.linkActiveOptions"
          (click)="handleButtonClick()"
        >
          {{ bannerAlertButton.text }}
        </fl-button>
      </fl-bit>
      <fl-button
        *ngIf="announcementBar && announcementBtnText"
        class="AnnouncementButton"
        [size]="ButtonSize.SMALL"
        [color]="ButtonColor.TRANSPARENT_LIGHT"
      >
        {{ announcementBtnText }}
      </fl-button>
      <fl-button
        *ngIf="closeable"
        class="AlertCloseButton"
        (click)="handleCloseEvent()"
      >
        <fl-icon
          name="ui-close"
          [color]="announcementBar ? IconColor.LIGHT : IconColor.MID"
        ></fl-icon>
      </fl-button>
    </fl-bit>
  `,
  styleUrls: ['./banner-alert.component.scss'],
  animations: [
    trigger('hideElement', [
      state(
        'visible',
        style({
          opacity: '1',
        }),
      ),
      state(
        'hidden',
        style({
          opacity: '0',
        }),
      ),
      transition('visible => hidden', animate('500ms ease-out')),
    ]),
  ],
})
export class BannerAlertComponent implements OnChanges {
  Margin = Margin;
  IconColor = IconColor;
  IconSize = IconSize;
  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  FontColor = FontColor;
  FontSize = FontSize;
  FontWeight = FontWeight;
  announcementBar = false;
  BannerAlertType = BannerAlertType;
  state = 'visible';

  @HostBinding('class.IsHidden') bannerAlertHidden = false;
  @Input() bannerAlertButton?: BannerAlertButton;
  @Input() type = BannerAlertType.DEFAULT;
  @Input() title?: string;
  @Input() closeable = true;
  @Input() announcementBtnText?: string;
  @Input() announcementIcon?: string;
  @Output() buttonClicked = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();

  ngOnChanges() {
    if (
      this.type === BannerAlertType.ANNOUNCEMENT_INFO ||
      this.type === BannerAlertType.ANNOUNCEMENT_CRITICAL ||
      this.type === BannerAlertType.ANNOUNCEMENT_SUCCESS
    ) {
      this.announcementBar = true;
    }
  }

  animationDone(event: any) {
    if (event.fromState !== 'void') {
      this.bannerAlertHidden = true;
    }
  }

  handleButtonClick() {
    this.buttonClicked.emit();
  }

  handleCloseEvent() {
    this.state = this.state === 'visible' ? 'hidden' : 'visible';
    this.close.emit();
  }
}
